import React, { useContext, useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { api } from 'services/api';
import { ActionButtonHead, GraficosFormat, GraficosRedondo, InputSpaceLabel } from 'styles/Global.styles';
import DatePicker from 'react-datepicker';
import ptBr from 'date-fns/locale/pt-BR';
import { ButtonOrcamento } from 'pages/kanban/Kanban.style';
import { LoadingContext } from '../../context/Loading/LoadingContext';
import ReplayIcon from '@mui/icons-material/Replay';
import IconButton from '@mui/material/IconButton';

const Dashboard = () => {
  const { setLoading, setLoadingMensagem, setLoadingBtn }: any = useContext(LoadingContext);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const pieChartRef = useRef<HTMLCanvasElement>(null); // Referência para o gráfico de pizza
  const quantityPieChartRef = useRef<HTMLCanvasElement>(null);
  const [quantityPieChartInstance, setQuantityPieChartInstance] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartInstance, setChartInstance] = useState(null);
  const [pieChartInstance, setPieChartInstance] = useState(null); // Estado para armazenar a instância do gráfico de pizza

  const [selectedClient, setSelectedClient] = useState<string>(''); // Estado para armazenar o ID do cliente selecionado
  const [clientes, setClientes] = useState([]); // Estado para armazenar a lista de clientes
  const [selectedVendedor, setSelectedVendedor] = useState<string>(''); // Estado para armazenar o ID do cliente selecionado
  const [vendedores, setVendedores] = useState([]); // Estado para armazenar a lista de clientes
  const [selectedPeriodo, setSelectedPeriodo] = useState<string>(''); // Estado para armazenar o ID do cliente selecionado
  const periodos = ['Bimestre', 'Trimestre', 'Semestre', 'Anual'];
  const dataAtual = new Date();
  const [dateRange, setDateRange] = useState([new Date(dataAtual.getTime() - 30 * 24 * 60 * 60 * 1000), dataAtual]);
  const [dateInicial, dateFinal] = dateRange;

  // Gráfico Torre
  const generateChart = (dados) => {
    if (!dados) return;
    const anos = Object.keys(dados);
    const precosFinais = anos?.map((ano) => dados[ano].total.precoFinal);

    // Adicionando os dados
    const datasetsLine = [
      {
        label: 'Feito',
        data: anos?.map((ano) => dados[ano].feito && dados[ano].feito.quantiodade),
      },
      {
        label: 'Follow',
        data: anos?.map((ano) => dados[ano].follow && dados[ano].follow.quantiodade),
      },
      {
        label: 'Aprovado',
        data: anos?.map((ano) => dados[ano].aprovado && dados[ano].aprovado.quantiodade),
      },
      {
        label: 'Recusado',
        data: anos?.map((ano) => dados[ano].recusado && dados[ano].recusado.quantiodade),
      },
      {
        label: 'Atualizar',
        data: anos?.map((ano) => dados[ano].atualizar && dados[ano].atualizar.quantiodade),
      },
      {
        label: 'Enviado',
        data: anos?.map((ano) => dados[ano].enviado && dados[ano].enviado.quantiodade),
      },
    ];

    // Array de cores
    const lineColors = [
      'rgba(255, 99, 132, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(75, 192, 192, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(255, 159, 64, 1)',
      'rgb(255, 64, 207, 1)',
    ];

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const chartInstance = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: anos,
        datasets: [
          {
            label: 'Total R$',
            data: precosFinais,
            backgroundColor: 'rgba(75, 192, 192, 0.6)',
            fill: true,
          },
          ...datasetsLine?.map((dataset, index) => ({
            ...dataset,
            backgroundColor: lineColors[index],
            fill: true,
          })),
        ],
      },
      // Habilitar o pan nos eixos X e Y
      options: {
        plugins: {
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              mode: 'y',
            },
            pan: {
              enabled: true,
              mode: 'y',
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    });
    return chartInstance;
  };

  // Gráfico circulo
  const generatePieChart = (totais) => {
    const labels = ['Total R$', 'Fretes R$', 'Descontos R$'];
    const values = [totais.somaTotal, totais.somaFrete, totais.somaDespesas];

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const pieChartInstance = new Chart(pieChartRef.current, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)', 'rgba(75, 192, 192, 1)'],
            borderWidth: 1,
          },
        ],
      },
    });

    return pieChartInstance;
  };

  const generateQuantityPieChart = (statusData) => {
    const labels = ['Feito', 'Atualizar', 'Follow', 'Enviado', 'Recusado', 'Aprovado'];
    const values = [
      statusData.feito,
      statusData.atualizar,
      statusData.follow,
      statusData.enviado,
      statusData.recusado,
      statusData.aprovado,
    ];

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const quantityPieChartInstance = new Chart(quantityPieChartRef.current, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: [
              'rgba(255, 99, 132, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 159, 64, 1)',
              'rgb(255, 64, 207, 1)',
            ],
            borderWidth: 1,
          },
        ],
      },
    });

    return quantityPieChartInstance;
  };

  const handleUpdateChart = () => {
    // Gerar o novo gráfico com os dados atualizados
    const newChartInstance = generateChart(chartData.dados);
    setChartInstance(newChartInstance);
  };

  const handleUpdatePieChart = () => {
    // Gerar o novo gráfico de pizza com os dados atualizados
    const newPieChartInstance = generatePieChart(chartData.totais);
    setPieChartInstance(newPieChartInstance);
  };

  const handleUpdateQuantityPieChart = () => {
    // Gerar o novo gráfico de pizza com os dados atualizados
    const newQuantityPieChartInstance = generateQuantityPieChart(chartData.totais.SomaStatus);
    setQuantityPieChartInstance(newQuantityPieChartInstance);
  };

  // Reload
  const handleReloadChart = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    setSelectedClient('');
    setSelectedVendedor('');
    setSelectedPeriodo('');
    setDateRange([new Date(dataAtual.getTime() - 30 * 24 * 60 * 60 * 1000), dataAtual]);
    // destroyer();
    // handleUpdateChart();
    // handleUpdatePieChart(); // Chamar a função para criar o gráfico de pizza
    // handleUpdateQuantityPieChart();
  };

  const handleClientChange = (event: any) => {
    setSelectedClient(event.target.value as string);
  };

  const handleVendedorChange = (event: any) => {
    setSelectedVendedor(event.target.value as string);
  };

  const handlePeriodoChange = (event: any) => {
    setSelectedPeriodo(event.target.value as string);
  };

  const loadingDash = async () => {
    setLoading(true);
    const baseQuery = `orcamento/dash/orcamentos?agrupar=${selectedPeriodo}&idVendedor=${selectedVendedor}&idCliente=${selectedClient}`;
    const queryFilter =
      dateInicial && dateFinal
        ? baseQuery + `&ini=${dateInicial?.toISOString()}&final=${dateFinal?.toISOString()}`
        : baseQuery;
    await api
      .get(queryFilter)
      .then((response) => {
        if (response.status) {
          const dados = response.results;
          setClientes(response.results.clientes);
          setVendedores(response.results.vendedores);
          setChartData(dados);
        } else {
          setLoadingMensagem(response.mensage);
          setLoadingBtn('OK');
        }
        setLoading(false);
      })
      .catch((e) => {
        const msg =
          e?.message === 'Unauthorized' ? 'Faça o login novamente! ' : 'Ops, Não pode ser carregado os dados!';
        setLoadingMensagem(msg);
        setLoadingBtn('OK');
      });
  };

  const destroyer = () => {
    if (chartInstance) {
      chartInstance.destroy();
    }
    if (pieChartInstance) {
      pieChartInstance.destroy(); // Destruir a instância anterior do gráfico de pizza antes de criar um novo
    }
    if (quantityPieChartInstance) {
      quantityPieChartInstance.destroy(); // Destruir a instância anterior do gráfico de pizza antes de criar um novo
    }
  };

  const handleFilter = () => {
    destroyer();
    loadingDash();
  };

  useEffect(() => {
    if (selectedVendedor || selectedClient) handleFilter();
  }, [selectedVendedor, selectedClient]);

  useEffect(() => {
    Chart.register(zoomPlugin);
    loadingDash();
  }, []);

  useEffect(() => {
    if (!chartData) return;
    handleUpdateChart();
    handleUpdatePieChart();
    handleUpdateQuantityPieChart(); // Chamar a função para criar o novo gráfico de pizza
  }, [chartData]);

  return (
    <>
      <ActionButtonHead>
        {/* Vendedores Select */}
        <InputSpaceLabel>
          <InputLabel id="selectVendedor-label">Vendedor</InputLabel>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="selectVendedor-label">{(selectedVendedor && 'Filtro') || 'Todos'}</InputLabel>
            <Select value={selectedVendedor} onChange={handleVendedorChange} label="Todos">
              <MenuItem key={-1} value="">
                <em>Limpar Filtro </em>
              </MenuItem>
              {vendedores?.map((vendedor) => (
                <MenuItem key={vendedor.id} value={vendedor.id}>
                  {vendedor.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </InputSpaceLabel>

        {/* DatePicker */}
        <InputSpaceLabel>
          <InputLabel id="selectDatalabel">Data</InputLabel>
          <FormControl fullWidth variant="outlined">
            <DatePicker
              selectsRange={true}
              startDate={dateInicial}
              endDate={dateFinal}
              dateFormat="dd/MM/yyyy"
              onChange={(date) => {
                setDateRange(date);
              }}
              locale={ptBr}
              placeholderText="Todos os Registros"
              withPortal
              isClearable={true}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              todayButton="Hoje"
            />
          </FormControl>
        </InputSpaceLabel>

        {/* Clientes Select */}
        <InputSpaceLabel>
          <InputLabel id="selectCliente-label">Clientes</InputLabel>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="selectCliente-label">{(selectedClient && 'Filtro') || 'Todos'}</InputLabel>
            <Select value={selectedClient} onChange={handleClientChange} label="Todos">
              <MenuItem key={-1} value="">
                <em>Limpar Filtro </em>
              </MenuItem>
              {clientes?.map((cliente) => (
                <MenuItem key={cliente.id} value={cliente.id}>
                  {cliente.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </InputSpaceLabel>

        {/* Periodo Select */}
        <InputSpaceLabel>
          <InputLabel id="selectPeriodoe-label">Período</InputLabel>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="selectPeriodo-label">{(selectedPeriodo && 'Filtro') || 'Mensal'}</InputLabel>
            <Select value={selectedPeriodo} onChange={handlePeriodoChange} label="Todos">
              <MenuItem key={-1} value="">
                <em>Limpar Filtro </em>
              </MenuItem>
              {periodos?.map((periodo, index) => (
                <MenuItem key={index} value={periodo}>
                  {periodo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </InputSpaceLabel>

        <ButtonOrcamento onClick={handleFilter}>Filtrar</ButtonOrcamento>

        <IconButton type="button" sx={{ p: '10px' }} aria-label="search" onClick={handleReloadChart}>
          <ReplayIcon />
        </IconButton>
      </ActionButtonHead>
      <GraficosFormat>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h4>Gráfico Geral</h4>
          <canvas
            ref={chartRef}
            height={400}
            style={{ cursor: 'move', width: '100%' }} // Adicionando estilo de cursor de "move" para indicar que o gráfico é movível
          />
        </div>
        <GraficosRedondo>
          <div>
            <h4>Balanço em R$</h4>
            <canvas ref={pieChartRef} width={200} height={400} />
          </div>
          <div>
            <h4>Balanço em quantidade</h4>
            <canvas ref={quantityPieChartRef} width={250} height={400} />
          </div>
        </GraficosRedondo>
      </GraficosFormat>
    </>
  );
};

export default Dashboard;
